import { Modal, Accordion, AccordionPanel } from '@sede-enterprise/shell-ds-react-framework';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getDealDetails, getNetCarbonIntensity } from '../../../api/services';
import { ASSET_SCOPE1_CI, GAS_FIRED_GENERATION, NATURAL_GAS } from '../../../api/constants';
import { getYears } from './DealDetailFunctions';
import { convertInputToCommaSeparatedWithThreeDecimal } from '../../../utils';
import InputForm from './InputForm/InputForm';
import { IDealDetails, IGroupNCI } from '../../../types/types';
import ModalKpiTable from './ModalKpiTable/ModalKpiTable';
import ModalFieldsTable from './ModalFieldsTable/ModalFieldsTable';
import KpiSummary from './KpiSummary/KpiSummary';
import { AxiosError } from 'axios';
import { showToast } from '../utils';
import { KpiFormMessage } from '../../ManualEntryForm/KpiFormMessage';
import { fivehundres, one, two } from '../../../constants';
import { useNetCarbonIntensity } from '../../../context/NetCarbonIntensityContext';

// Helper function to normalize EVP name for comparison
const normalizeEvpName = (evpName: string): string => {
  return evpName.toLowerCase().replace(/[^a-z0-9]/g, '');
};

const Container = styled.div`
  overflow: scroll;
`;
interface DealDetailsProps {
  isOpen: boolean;
  opportunityId: number;
  operatingPlan: number | undefined;
  onClose: () => void;
}
const DealDetails: React.FC<DealDetailsProps> = ({
  isOpen,
  opportunityId,
  operatingPlan,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dealDetailsData, setDealDetailsData] = useState<IDealDetails[]>([]);
  const [netCarbonIntensityData, setNetCarbonIntensityData] = useState<IGroupNCI[]>([]);
  const [kpiTableDataFromApi, setKpiTableDataFromApi] = useState<any[]>([]);
  const [years, setYears] = useState<string[]>([]);
  let { netCarbonIntensityData: nciData } = useNetCarbonIntensity();

  const getDealDetailsData = async () => {
    setIsLoading(true);
    try {
      const dealDetailsRes = await getDealDetails(opportunityId);
      setDealDetailsData(dealDetailsRes?.data);
      setYears(getYears(dealDetailsRes?.data));
      if (nciData.length === one) {
        // make the api call to get netcarbonintensity data if it is not available
        const nciRes = await getNetCarbonIntensity();
        nciData = nciRes?.data;
      }
      // Use Net Carbon Intensity API response directly (do not derive NCI from deal calculations)
      setNetCarbonIntensityData(nciData);

      // Build KPI table data from API response (use server-provided KPI fields)
      const yearsLocal = getYears(dealDetailsRes?.data);
      const isTSProducts = (dealDetailsRes?.data?.[0]?.EVP_NAME || '').toLowerCase().replace(/[^a-z0-9]/g, '') === 'tsproducts';
      // Detect if any record indicates Gas Fired Generation tech source using same logic as calculator
      const isGasFired = Array.isArray(dealDetailsRes?.data) && dealDetailsRes.data.some((d: any) => {
        const tech = (d.TECHNOLOGY_SOURCE_NAME || '').toString();
        return tech.includes(NATURAL_GAS) || tech.includes('LNG');
      });
      const buildKpiTableDataFromApi = (data: any[], yearsArr: string[]) => {
        const rows: any[] = [];
        const labels = isTSProducts
          ? ['Operated Scope 1&2 (MTPA)', 'Equity Scope 1&2 (MTPA)', 'Net Absolute Emissions (MTPA)', 'Volume-weighted Avg Carbon Intensity (KTPA)', 'Scope 3 Category 11 Emission (KTPA)', 'NCI Impact (gCO2e/MJ)']
          : ['Operated Scope 1&2 (MTPA)', 'Equity Scope 1&2 (MTPA)', 'Net Absolute Emissions (MTPA)', 'Avg Carbon Intensity**', 'NCI Impact (gCO2e/MJ)'];

        // If this deal contains a Gas Fired Generation tech source, include Scope 1 Combustion Carbon Intensity at the top
        if (isGasFired) {
          labels.unshift(ASSET_SCOPE1_CI);
        }

        labels.forEach((label) => {
          const obj: any = { KPI: label, Total: '0' };
          let totalForRow = 0;
          let hasNumeric = false;
          yearsArr.forEach((y) => {
            const yearNum = Number(y);
            // Find first matching record for the year
            const rec = data.find((d) => Number(d.YEAR) === yearNum);
            let val: any = null;
            switch (label) {
              case ASSET_SCOPE1_CI:
                // Use SCOPE_COMBUSTION_CI value provided by deal details API
                val = rec?.SCOPE_COMBUSTION_CI ?? null;
                break;
              case 'Operated Scope 1&2 (MTPA)':
                val = rec?.OPERATED_SCOPE ?? null;
                break;
              case 'Equity Scope 1&2 (MTPA)':
                val = rec?.EQUITY_SCOPE ?? null;
                break;
              case 'Net Absolute Emissions (MTPA)':
                val = rec?.NET_ABSOLUTE_EMISSION ?? null;
                break;
              case 'Avg Carbon Intensity**':
                val = rec?.AVG_CARBON_INTENSITY ?? null;
                break;
              case 'Volume-weighted Avg Carbon Intensity (KTPA)':
                val = rec?.VOL_WEIGHTED_AVG_CI ?? null;
                break;
              case 'Scope 3 Category 11 Emission (KTPA)':
                val = rec?.SCOPE3_CAT11_EMISSION ?? null;
                break;
              case 'NCI Impact (gCO2e/MJ)':
                val = rec?.NCI_IMPACT ?? null;
                break;
              default:
                val = null;
            }
            if (val === null || val === undefined || val === '') {
              obj[y] = 'NA';
            } else {
              const num = typeof val === 'number' ? val : Number(val);
              if (!isNaN(num)) {
                hasNumeric = true;
                totalForRow += num;
                obj[y] = convertInputToCommaSeparatedWithThreeDecimal(num.toFixed(3));
              } else {
                obj[y] = String(val);
              }
            }
          });
          // Compute Total for the row if we encountered numeric year values
          if (hasNumeric) {
            obj['Total'] = convertInputToCommaSeparatedWithThreeDecimal(totalForRow.toFixed(3));
          } else {
            obj['Total'] = '0';
          }
          rows.push(obj);
        });
        return rows;
      };

      const kpiTableDataFromApi = buildKpiTableDataFromApi(dealDetailsRes?.data ?? [], yearsLocal);
      // Store KPI table data in state for render
      setKpiTableDataFromApi(kpiTableDataFromApi);
    } catch (err) {
      const error = err as AxiosError<Error>;
      showToast(error?.response?.status ?? fivehundres, 'Error while fetching deal details.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDealDetailsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);
  return (
    <Modal
      loading={isLoading}
      loadingText={'Deal details loading'}
      open={isOpen}
      width={'97%'}
      contentScrollable={true}
      title={'Deal Summary'}
      onClose={onClose}
    >
      <Container>
        {dealDetailsData?.length > 0 && (
          <Accordion activeKey={[0, one, two]}>
            <AccordionPanel
              className='accordion-box-shadow'
              header={
                <div>
                  <span className='acc-label'>
                    <b>STEP 01 &nbsp;&nbsp;</b>
                  </span>
                  <span className='sub-heading'>Deal Entry Form &nbsp;</span>
                </div>
              }
            >
              <InputForm selectConfig={dealDetailsData} />
              {(() => {
                const evpName = dealDetailsData[0]?.EVP_NAME ?? '';
                return normalizeEvpName(evpName) !== 'tsproducts';
              })() && <KpiFormMessage />}
            </AccordionPanel>
            <AccordionPanel
              className='accordion-box-shadow'
              header={
                <div>
                  <span className='acc-label'>
                    <b>STEP 02 &nbsp;&nbsp;</b>
                  </span>
                  <span className='sub-heading'>Input Fields Table &nbsp;</span>
                </div>
              }
            >
              <ModalFieldsTable dealDetailsData={dealDetailsData} years={years} />
            </AccordionPanel>
            <AccordionPanel
              className='accordion-box-shadow'
              header={
                <div>
                  <span className='acc-label'>
                    <b>STEP 03 &nbsp;&nbsp;</b>
                  </span>
                  <span className='sub-heading'>Carbon KPIs &nbsp;</span>
                </div>
              }
            >
              {dealDetailsData && netCarbonIntensityData && (() => {
                // Calculate KPI table data once for both components
                const Cols = ['KPI', ...years, 'Total'].map((val) => ({
                  accessorKey: val,
                  header: val,
                }));
                const kpiTableData = kpiTableDataFromApi.length ? kpiTableDataFromApi : [];
                
                return (
                  <>
                    <KpiSummary
                      dealDetailsData={dealDetailsData}
                      netCarbonIntensityData={netCarbonIntensityData}
                      kpiTableData={kpiTableData}
                    />
                    <ModalKpiTable
                      dealDetailsData={dealDetailsData}
                      years={years}
                      netCarbonIntensityData={netCarbonIntensityData}
                      kpiTableData={kpiTableData}
                    />
                  </>
                );
              })()}
            </AccordionPanel>
          </Accordion>
        )}
      </Container>
    </Modal>
  );
};

export default DealDetails;
